import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/admin/audits")({
  component: AuditsPage,
  head: () => ({ meta: [{ title: "Visibility Audit Reports - KCN Admin" }, { name: "robots", content: "noindex,nofollow" }] }),
});

type Row = {
  id: string;
  agent_name: string;
  agent_email: string | null;
  market: string | null;
  notes: string | null;
  file_path: string;
  file_name: string;
  file_size: number;
  uploaded_by_name: string | null;
  created_at: string;
};

const MAX_BYTES = 20 * 1024 * 1024;

function prettySize(bytes: number) {
  if (!bytes) return "-";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
}

function AuditsPage() {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [q, setQ] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [agentName, setAgentName] = useState("");
  const [agentEmail, setAgentEmail] = useState("");
  const [market, setMarket] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState<File | null>(null);

  async function load() {
    const { data, error } = await supabase
      .from("visibility_audit_reports")
      .select("id, agent_name, agent_email, market, notes, file_path, file_name, file_size, uploaded_by_name, created_at")
      .order("created_at", { ascending: false })
      .limit(500);
    if (error) setErr(error.message);
    else setRows((data ?? []) as Row[]);
  }

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (!rows) return null;
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(
      (r) =>
        r.agent_name.toLowerCase().includes(s) ||
        (r.agent_email ?? "").toLowerCase().includes(s) ||
        (r.market ?? "").toLowerCase().includes(s),
    );
  }, [rows, q]);

  async function onUpload(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!agentName.trim()) return setErr("Add the agent name.");
    if (!file) return setErr("Choose a PDF report to upload.");
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      return setErr("Only PDF files can be uploaded.");
    }
    if (file.size > MAX_BYTES) return setErr("That file is larger than 20 MB.");

    setBusy(true);
    try {
      const { data: userRes } = await supabase.auth.getUser();
      const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, "-");
      const path = `${new Date().getFullYear()}/${crypto.randomUUID()}-${safe}`;
      const up = await supabase.storage.from("audit-reports").upload(path, file, {
        contentType: "application/pdf",
        upsert: false,
      });
      if (up.error) throw new Error(up.error.message);

      const ins = await supabase.from("visibility_audit_reports").insert({
        agent_name: agentName.trim(),
        agent_email: agentEmail.trim() || null,
        market: market.trim() || null,
        notes: notes.trim() || null,
        file_path: path,
        file_name: file.name,
        file_size: file.size,
        uploaded_by: userRes.user?.id ?? null,
        uploaded_by_name: userRes.user?.email ?? null,
      });
      if (ins.error) {
        await supabase.storage.from("audit-reports").remove([path]);
        throw new Error(ins.error.message);
      }

      setAgentName("");
      setAgentEmail("");
      setMarket("");
      setNotes("");
      setFile(null);
      (document.getElementById("audit-file") as HTMLInputElement | null)?.value &&
        ((document.getElementById("audit-file") as HTMLInputElement).value = "");
      await load();
    } catch (e: any) {
      setErr(e.message ?? "Upload failed.");
    } finally {
      setBusy(false);
    }
  }

  async function onOpen(row: Row) {
    setErr(null);
    const { data, error } = await supabase.storage.from("audit-reports").createSignedUrl(row.file_path, 300);
    if (error || !data) return setErr(error?.message ?? "Could not open that file.");
    window.open(data.signedUrl, "_blank", "noopener");
  }

  async function onDelete(row: Row) {
    if (!confirm(`Delete the audit report for ${row.agent_name}?`)) return;
    setErr(null);
    const del = await supabase.from("visibility_audit_reports").delete().eq("id", row.id);
    if (del.error) return setErr(del.error.message);
    await supabase.storage.from("audit-reports").remove([row.file_path]);
    await load();
  }

  return (
    <div>
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.24em] text-foreground/55">Internal only</p>
        <h1 className="headline-md mt-2">Visibility audit findings</h1>
        <p className="mt-2 max-w-2xl text-foreground/70">
          Upload the finished audit report for an agent. Files are private to the admin team and open through a short-lived secure link.
        </p>
      </div>

      <form onSubmit={onUpload} className="mb-10 border border-border bg-surface p-6">
        <div className="grid gap-4 md:grid-cols-3">
          <label className="block">
            <span className="mb-1 block text-xs uppercase tracking-[0.16em] text-foreground/65">Agent name</span>
            <input
              value={agentName}
              onChange={(e) => setAgentName(e.target.value)}
              maxLength={120}
              className="w-full border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs uppercase tracking-[0.16em] text-foreground/65">Email</span>
            <input
              value={agentEmail}
              onChange={(e) => setAgentEmail(e.target.value)}
              maxLength={255}
              className="w-full border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs uppercase tracking-[0.16em] text-foreground/65">Market</span>
            <input
              value={market}
              onChange={(e) => setMarket(e.target.value)}
              maxLength={120}
              className="w-full border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
            />
          </label>
        </div>
        <label className="mt-4 block">
          <span className="mb-1 block text-xs uppercase tracking-[0.16em] text-foreground/65">Notes</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={1000}
            rows={2}
            className="w-full border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
          />
        </label>
        <label className="mt-4 block">
          <span className="mb-1 block text-xs uppercase tracking-[0.16em] text-foreground/65">PDF report (max 20 MB)</span>
          <input
            id="audit-file"
            type="file"
            accept="application/pdf,.pdf"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="w-full border border-border bg-background px-3 py-2 text-sm file:mr-4 file:border-0 file:bg-primary file:px-4 file:py-2 file:text-xs file:uppercase file:tracking-[0.16em] file:text-primary-foreground"
          />
        </label>
        {err ? <p className="mt-4 text-sm text-primary">{err}</p> : null}
        <button
          type="submit"
          disabled={busy}
          className="mt-6 bg-primary px-6 py-3 text-xs uppercase tracking-[0.18em] text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
        >
          {busy ? "Uploading…" : "Upload report"}
        </button>
      </form>

      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <input
          placeholder="Search agent, email or market"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-72 border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
        />
      </div>

      {!filtered ? (
        <p className="text-foreground/65">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="text-foreground/65">No audit reports uploaded yet.</p>
      ) : (
        <div className="border border-border">
          <table className="w-full text-sm">
            <thead className="bg-surface text-xs uppercase tracking-[0.16em] text-foreground/65">
              <tr>
                <th className="p-3 text-left">Agent</th>
                <th className="p-3 text-left">Market</th>
                <th className="p-3 text-left">File</th>
                <th className="p-3 text-left">Uploaded</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-border align-top">
                  <td data-label="Agent" className="p-3">
                    <div className="font-medium">{r.agent_name}</div>
                    <div className="text-xs text-foreground/55">{r.agent_email}</div>
                    {r.notes ? <div className="mt-1 max-w-md text-xs text-foreground/55">{r.notes}</div> : null}
                  </td>
                  <td data-label="Market" className="p-3 text-foreground/75">{r.market}</td>
                  <td data-label="File" className="p-3 text-foreground/75">
                    {r.file_name}
                    <div className="text-xs text-foreground/55">{prettySize(r.file_size)}</div>
                  </td>
                  <td data-label="Uploaded" className="p-3 text-foreground/75">
                    {new Date(r.created_at).toLocaleDateString()}
                    <div className="text-xs text-foreground/55">{r.uploaded_by_name}</div>
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button onClick={() => onOpen(r)} className="mr-4 text-xs uppercase tracking-[0.16em] text-foreground/75 hover:text-foreground">
                      Open
                    </button>
                    <button onClick={() => onDelete(r)} className="text-xs uppercase tracking-[0.16em] text-primary hover:underline">
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
